import { useMemo, useState } from "react";
import { SegmentedToggle } from "../../components/ui/SegmentedToggle";
import { GrammaticaCard } from "../../components/study/GrammaticaCard";
import {
  listGrammatica,
  type ExamFacet,
  type GrammaticaEntry,
} from "../../data/study/grammatica-index";
import { useT } from "../../lib/use-t";

type Filter = "all" | ExamFacet;

export function GrammaticaIndexPage() {
  const t = useT();
  const [filter, setFilter] = useState<Filter>("all");
  const entries = useMemo(() => listGrammatica(), []);

  const helpLabel = (e: GrammaticaEntry): string => {
    if (e.exam === "schrijven") return t("grammatica.index.helpSchrijven");
    // All current spreken topics are general (deelen 1–4); a single-deel topic
    // would read "Helpt bij Deel N".
    if (e.relevantDeelen.length === 1) {
      return t("grammatica.index.helpDeelFmt", { deel: e.relevantDeelen[0] });
    }
    return t("grammatica.index.helpAlgemeen");
  };

  const visible = entries.filter((e) => filter === "all" || e.exam === filter);

  return (
    <div className="mx-auto w-full max-w-[1080px] px-5 sm:px-6 py-6 sm:py-8 pb-24 sm:pb-28">
      <header className="space-y-2">
        <p className="font-mono-badge text-xs uppercase tracking-wider text-[var(--ink-faint)]">
          {t("grammatica.index.eyebrow")}
        </p>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">
          {t("grammatica.index.title")}
        </h1>
        <p className="text-sm text-[var(--ink-muted)] max-w-[60ch] leading-relaxed">
          {t("grammatica.index.description")}
        </p>
      </header>

      <div className="mt-6 mb-5 flex flex-wrap items-center gap-3">
        <SegmentedToggle<Filter>
          ariaLabel={t("grammatica.index.eyebrow")}
          value={filter}
          onChange={setFilter}
          options={[
            { value: "all", label: t("grammatica.index.filterAll") },
            { value: "spreken", label: t("grammatica.index.filterSpreken") },
            { value: "schrijven", label: t("grammatica.index.filterSchrijven") },
          ]}
        />
        <span className="ml-auto font-mono-badge text-xs text-[var(--ink-faint)] tabular-nums">
          {t("grammatica.index.countTotalFmt", { count: visible.length })}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4">
        {visible.map((e, i) => (
          <GrammaticaCard
            key={e.slug}
            slug={e.slug}
            index={i + 1}
            title={e.title}
            tagline={e.tagline}
            duration={e.duration}
            helpLabel={helpLabel(e)}
          />
        ))}
      </div>
    </div>
  );
}
